// The one place that turns dates, numbers, sizes and durations into text.
import { cache } from "react";

type DateInput = Date | string | number | null | undefined;
/** `abbr` appends the zone abbreviation ("14:05 CEST"); use it for deadlines and session times. */
type Opts = { tz?: string; abbr?: boolean };

export function isValidTimeZone(name: string | null | undefined): name is string {
  if (!name) return false;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: name });
    return true;
  } catch {
    return false;
  }
}

/** Saved choice wins, then the browser zone from the cookie, then UTC. */
export function pickTimeZone(saved: string | null | undefined, cookie: string | null | undefined): string {
  if (isValidTimeZone(saved)) return saved;
  if (isValidTimeZone(cookie)) return cookie;
  return "UTC";
}

// Server (react-server layer): React.cache gives a store per request. Elsewhere (SSR, browser)
// cache() is a pass-through, so the module variable below is used.
// ponytail: the SSR module variable is shared by concurrent renders; a mixed-up first paint is
// corrected at hydration. Move to AsyncLocalStorage if that ever shows up.
const requestStore = cache(() => ({ tz: undefined as string | undefined }));
let clientTz: string | undefined;

export function setRequestTimeZone(tz: string) {
  requestStore().tz = tz;
}
export function setClientTimeZone(tz: string) {
  clientTz = tz;
}
function currentTz(): string {
  return requestStore().tz ?? clientTz ?? "UTC";
}

const LOCALE = "en-GB";

function toDate(v: DateInput): Date | null {
  if (v === null || v === undefined || v === "") return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmt(v: DateInput, o: Intl.DateTimeFormatOptions, opts?: Opts): string {
  const d = toDate(v);
  if (!d) return "";
  const tz = opts?.tz ?? currentTz();
  const withZone = opts?.abbr ? { ...o, timeZoneName: "short" as const } : o;
  const out = new Intl.DateTimeFormat(LOCALE, tz === "local" ? withZone : { ...withZone, timeZone: tz }).format(d);
  return out.replace("Sept", "Sep");
}

/** 6 Oct 2026 */
export const formatDate = (v: DateInput, opts?: Opts) => fmt(v, { day: "numeric", month: "short", year: "numeric" }, opts);
/** 6 Oct (year added when it differs from `now`) */
export function formatDateShort(v: DateInput, now: DateInput = new Date(), opts?: Opts) {
  const d = toDate(v);
  const n = toDate(now);
  const sameYear = !d || !n || fmt(d, { year: "numeric" }, opts) === fmt(n, { year: "numeric" }, opts);
  return fmt(v, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) }, opts);
}
/** Tue, 6 Oct (add `year: true` for Tue, 6 Oct 2026) */
export const formatWeekdayDate = (v: DateInput, o?: Opts & { year?: boolean }) =>
  fmt(v, { weekday: "short", day: "numeric", month: "short", ...(o?.year ? { year: "numeric" } : {}) }, o);
/** Tuesday, 6 October */
export const formatDateLong = (v: DateInput, opts?: Opts) => fmt(v, { weekday: "long", day: "numeric", month: "long" }, opts);
/** October 2026 */
export const formatMonth = (v: DateInput, opts?: Opts) => fmt(v, { month: "long", year: "numeric" }, opts);
/** 14:05 */
export const formatTime = (v: DateInput, opts?: Opts) => fmt(v, { hour: "2-digit", minute: "2-digit", hour12: false }, opts);
/** 6 Oct 2026, 14:05 */
export const formatDateTime = (v: DateInput, opts?: Opts) =>
  fmt(v, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }, opts);

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Lower-case unless `capitalize`: "just now", "5 min ago", "3 h ago", "yesterday", "4 days ago", then a short date. Future: "in 5 min", "tomorrow", "in 3 days". */
export function formatRelative(v: DateInput, now: DateInput = new Date(), o?: { capitalize?: boolean }): string {
  const out = relative(v, now);
  return o?.capitalize ? out.charAt(0).toUpperCase() + out.slice(1) : out;
}

function relative(v: DateInput, now: DateInput): string {
  const d = toDate(v);
  const n = toDate(now);
  if (!d || !n) return "";
  const diff = n.getTime() - d.getTime();
  const abs = Math.abs(diff);
  const past = diff >= 0;
  const wrap = (s: string) => (past ? `${s} ago` : `in ${s}`);
  if (abs < MIN) return "just now";
  if (abs < HOUR) return wrap(`${Math.floor(abs / MIN)} min`);
  if (abs < DAY) return wrap(`${Math.floor(abs / HOUR)} h`);
  const days = Math.floor(abs / DAY);
  if (days === 1) return past ? "yesterday" : "tomorrow";
  if (days < 7) return wrap(`${days} days`);
  return formatDateShort(d, n);
}

/** 12,345 */
export const formatNumber = (n: number, maxFractionDigits = 0) =>
  new Intl.NumberFormat(LOCALE, { maximumFractionDigits: maxFractionDigits }).format(n);

/** 12% from 0.12 */
export const formatPercent = (ratio: number, maxFractionDigits = 0) =>
  new Intl.NumberFormat(LOCALE, { style: "percent", maximumFractionDigits: maxFractionDigits }).format(ratio);

/** 512 B, 1.5 KB, 3.2 MB, 1.1 GB */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

/** Planned training time, in minutes: "45 min", "2 h", "2.5 days" (a day is 8 h). */
export function formatDuration(minutes: number | null | undefined): string {
  if (!minutes) return "0 min";
  if (minutes >= 480 && minutes % 240 === 0) {
    const days = minutes / 480;
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    return `${minutes / 60} h`;
  }
  return `${minutes} min`;
}

/** Logged time as people say it: 90 → "1 h 30", 60 → "1 h", 45 → "45 min". */
export function hoursLabel(minutes: number) {
  const h = Math.floor(minutes / 60), m = minutes % 60;
  return minutes === 0 ? "0 h" : h === 0 ? `${m} min` : m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}

/** Totals read better in hours: 2250 → "37.5 h", 45 → "0.8 h". */
export function formatHours(minutes: number): string {
  return `${Math.round(minutes / 6) / 10} h`;
}

/** Elapsed milliseconds as "2d 3h", "4h 10m", "7m". */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / MIN));
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const minutes = total % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Text for an email or notification: the recipient's saved zone, else UTC with an explicit "UTC" label. */
export function formatForRecipient(v: DateInput, recipientTz: string | null | undefined, o?: { time?: boolean }): string {
  const known = isValidTimeZone(recipientTz);
  const tz = known ? recipientTz : "UTC";
  if (o?.time) return formatDateTime(v, { tz, abbr: true });
  return known ? formatDate(v, { tz }) : `${formatDate(v, { tz })} UTC`;
}
